import classNames from 'classnames';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ContextMenu, ContextMenuTrigger, ContextMenuTriggerProps } from 'react-contextmenu';
import { promiseNoop } from '../../../utilities/noop';
import {
  ColumnKey,
  ColumnScope,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { Context } from '../../../services/_globalContext/context';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import s from '../../playlist/playlist.module.scss';
import FadilaMenuItem from '../contextMenu/fadilaMenuItem';
import useViewOptions from '../viewOptions/useViewOptions';
import useColumnResizing from '../hooks/useColumnResizing';
import useColumnSettings from '../hooks/useColumnSettings';
import useDeductedContentWidth from '../hooks/useDeductedContentWidth';
import useSharedPlaylistMethods from '../hooks/useSharedPlaylistMethods';
import ItemListColumn from '../itemListColumn/itemListColumn';
import PlaylistControls from '../playlistControls/playlistControls';
import Spinner from '../spinner/spinner';
import InfiniteList from '../virtualized/infiniteList';
import { SearchResultSort, sortResults, SortValue } from './searchResultSorting';
import StreamingServiceSearchResultRenderer, {
  StreamingServiceSearchResulData as StreamingServiceSearchResultData,
} from './streamSvcSearchRenderer';

/**
 * The selection handlers handed to each row, with the shared selection ref
 * already bound.
 */
interface ItemHandlers<T1> {
  handleToggleItem(itm: T1): void;
  handleShiftSelect(itm: T1): void;
  changeItemSelectionState(itm: T1, selectState: boolean, remPrev: boolean | undefined): void;
}

interface Props<T1, T2> {
  columnScope: ColumnScope;
  loading: boolean;
  results: T1[];
  itemComponent: React.FunctionComponent<T2>;

  display: boolean;
  isComplete(): boolean;
  totalItemCount(): number;
  totalItemCountString?: string | false;
  filteredItemCountString?: string | false;
  columnCustomName?: (colKey: ColumnKey) => string | undefined;

  isNextPageLoading: boolean;
  loadMore(startIndex: number, stopIndex: number): Promise<any>;
  /**
   * Is there more data left to be loaded?
   */
  hasMore?: boolean;

  props: Partial<T2>;
  /**
   * Virtualized row height
   */
  itemSize?: number;
  /**
   * What an item's value is for a given column, which is what makes the
   * headers sortable. Omit it and the headers stay inert, which is what
   * every list here did before.
   *
   * Build it once - `createTrackSortValue` caches per item - or every render
   * throws the cache away. See searchResultSorting.ts.
   */
  sortValue?: SortValue<T1>;
}

function StreamingServiceSearchResults<T1, T2>({
  columnScope,
  loading,
  results,
  itemComponent,
  display,
  isComplete,
  totalItemCount: totalItemCountF,
  totalItemCountString,
  filteredItemCountString,
  columnCustomName,
  isNextPageLoading,
  loadMore,
  hasMore,
  props,
  itemSize,
  sortValue,
}: Props<T1, T2>) {
  const ctx = useContext(Context);
  const { columns, columnSettings } = useColumnSettings(columnScope);

  /**
   * Which column the results are sorted by, which way, and - the part that
   * is not obvious - which list it was chosen for.
   *
   * A sort belongs to one search. Carrying it onto the next query's answers
   * would bury the relevance order the service sent, and there is no "sort by
   * nothing" to click back to. Rather than watching for a new search and
   * clearing this, the sort simply stops applying once the list it was made
   * for is gone: appliesTo is the first result at the time, and paging
   * appends, so the head of the list is the one thing a new page never
   * changes.
   *
   * Derived rather than cleared. The first attempt did it during render -
   * mutate a ref holding the previous first result, call setSort when it
   * changed - and it worked about half the time: StrictMode re-invokes the
   * body, the second pass sees the ref already updated by the first, and the
   * reset never happens. Nothing to get wrong if there is nothing to reset.
   */
  const [sort, setSort] = useState<{
    sort: SearchResultSort;
    appliesTo: T1 | undefined;
  }>();

  const activeSort = sort && sort.appliesTo === results[0] ? sort.sort : undefined;

  /**
   * Sorts what has been loaded, which is all a paged list can do. Scrolling
   * on pulls the next page in and re-sorts, so the order stays right as the
   * list grows; the paging itself is unaffected, because loadMore appends to
   * the page's own array rather than to this one.
   */
  const sortedResults = useMemo(
    () => sortResults(results, activeSort, sortValue),
    [results, activeSort, sortValue],
  );

  // Read by the click handler, which is built once and would otherwise close
  // over the first render's results.
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const sortOnClick = useCallback(function sortOnClick(prop: TrackProperty) {
    return function onSort(ev: React.MouseEvent) {
      ev.stopPropagation();
      ev.preventDefault();

      setSort((current) => {
        const appliesTo = resultsRef.current[0];
        // A sort left over from the previous search counts as no sort, so
        // the first click on a column after searching again starts ascending
        // rather than reversing something invisible.
        const currentSort = current && current.appliesTo === appliesTo ? current.sort : undefined;

        return {
          appliesTo,
          // Same column again reverses it, a new column starts ascending -
          // what the playlist headers do.
          sort:
            currentSort && currentSort.prop === prop
              ? {
                  prop,
                  direction: currentSort.direction === 'ascending' ? 'descending' : 'ascending',
                }
              : { prop, direction: 'ascending' },
        };
      });
    };
  }, []);

  const {
    totalContentWidth,
    mainViewport,
    setMainViewport,
    resetColumnWidths,
    columnAutoResize,
    hideColumn,
    unhideColumn,
  } = useDeductedContentWidth(columnScope);
  const { openViewOptions, viewOptionsDialog } = useViewOptions(columnScope, {
    hideColumn,
    unhideColumn,
    resetColumnWidths,
  });
  const [onResizeStart] = useColumnResizing(columnScope, mainViewport);

  const {
    selectedItems,
    selectedItemsRef,

    handleToggleItem,
    shiftSelectItem,
    changeItemSelectionState,
    clearSelected,
  } = useSharedPlaylistMethods({
    tracks: sortedResults,
    playlistElement: mainViewport,
  });

  const [contextTriggers] = useState<
    {
      [P in TrackProperty]?: React.Component<ContextMenuTriggerProps, any, any>;
    } & {
      size?: React.Component<ContextMenuTriggerProps, any, any>;
      image?: React.Component<ContextMenuTriggerProps, any, any>;
      contextCol?: React.Component<ContextMenuTriggerProps, any, any>;
    }
  >({});

  const toggleMenu = useCallback(
    (e: React.MouseEvent) => {
      const parent = e.currentTarget.parentElement;

      if (parent) {
        const columnName = parent.attributes.getNamedItem('data-column-name')
          ?.value as TrackProperty;

        const ctxTrigger = contextTriggers[columnName];
        if (ctxTrigger) {
          (ctxTrigger as any).handleContextClick(e);
        }
      }
    },
    [contextTriggers],
  );

  // Adds for the conditional loading element that follows if we do not have a total item count
  const totalVirtualItemCount = hasMore ? 1 : 0; // Loading element at end

  const isItemLoaded = useCallback(
    (index: number) =>
      // Don't include loading element padding
      index < results.length,
    [results],
  );

  // Typed through the type argument rather than parameter annotations: the
  // exhaustive-deps rule this project runs mistakes a type parameter
  // referenced inside a hook callback for a missing runtime dependency.
  const itemHandlers = useMemo<ItemHandlers<T1>>(
    () => ({
      handleToggleItem: (itm) => handleToggleItem(itm, selectedItemsRef),
      handleShiftSelect: (itm) => shiftSelectItem(itm, selectedItemsRef, sortedResults),
      changeItemSelectionState: (itm, selectState, remPrev) =>
        changeItemSelectionState(itm, selectedItemsRef, selectState, remPrev),
    }),
    [selectedItemsRef, handleToggleItem, shiftSelectItem, changeItemSelectionState, sortedResults],
  );

  const itemRendererProps: StreamingServiceSearchResultData<T1, T2> = useMemo(
    () => ({
      results: sortedResults,

      itemComponent,

      selectedItems,
      portalTarget: mainViewport || undefined,

      props: {
        selectedItemsRef,
        ...itemHandlers,

        clearSelected,

        ...props,
      },
    }),
    [
      selectedItems,
      selectedItemsRef,
      mainViewport,
      itemHandlers,
      clearSelected,
      sortedResults,
      props,
      itemComponent,
    ],
  );

  return (
    <article ref={(c) => setMainViewport(c || undefined)} className={`items-list ${s.playlist}`}>
      {loading || columnSettings === undefined || mainViewport === undefined ? (
        <Spinner />
      ) : (
        <PlaylistControls
          name="Search results"
          display={display}
          isComplete={isComplete}

          filteredItemCount={results.length}
          totalItemCount={totalItemCountF}

          displayGetRemaining={false}

          /*
            A sort can only order what has been fetched, and these lists
            fetch as you scroll - so say so rather than letting the first
            screenful look like the whole answer sorted. It appears only when
            both halves of the confusion are present: something is sorted and
            there is more to come.
          */
          additionalControls={
            activeSort && hasMore ? (
              <span
                className={s.sortScopeNote}
                title="More results load as you scroll, and join the sort as they arrive."
              >
                {`sorted - ${results.length} loaded`}
              </span>
            ) : undefined
          }

          displayBottom={ctx.settings.showPlaylistStatusBar}
          totalItemCountString={totalItemCountString}
          filteredItemCountString={filteredItemCountString}
        >
          <div className={classNames(s.contentWrapper, s.parentContentWrapper)}>
            <div className={s.thead} style={{ width: totalContentWidth }}>
              <div className={classNames(s.headerColumn, s.underscoreColumn)}></div>
              {columns.map((colKey) => (
                <ItemListColumn
                  key={colKey}
                  customName={columnCustomName && columnCustomName(colKey)}
                  columnKey={colKey}
                  // size and image are not track properties and there is
                  // nothing to order a list by in them.
                  onSort={
                    sortValue && colKey !== 'size' && colKey !== 'image'
                      ? sortOnClick(colKey)
                      : undefined
                  }
                  sortDirection={
                    activeSort && activeSort.prop === colKey ? activeSort.direction : undefined
                  }
                  columnSettings={columnSettings}
                  columnAutoResize={columnAutoResize}
                  contextMenuTriggerRef={(c) => {
                    contextTriggers[colKey] = c || undefined;
                  }}
                  onResizeStart={onResizeStart}
                  toggleMenu={toggleMenu}
                />
              ))}
              <div
                data-column-name="contextCol"
                className={classNames(s.headerColumn, s.hideOnHover, s.contextMenuBtnColumn)}
                style={{ width: (ctx.scrollbarWidth || 0) + 16 }}
              >
                <ContextMenuTrigger
                  ref={(c) => {
                    contextTriggers['contextCol'] = c || undefined;
                  }}
                  id="cloudprovider-context-menu"
                  holdToDisplay={-1}
                  disableIfShiftIsPressed={true}
                >
                  <></>
                </ContextMenuTrigger>
                <button className={classNames(s.menuBtn, 'invisible-control')} onClick={toggleMenu}>
                  ☰
                </button>
              </div>
              {viewOptionsDialog}
              <ContextMenu id="cloudprovider-context-menu">
                <FadilaMenuItem onClick={preventDefaultStopPropogation(openViewOptions)}>
                  View options…
                </FadilaMenuItem>
                <FadilaMenuItem onClick={preventDefaultStopPropogation(resetColumnWidths)}>
                  Reset columns
                </FadilaMenuItem>
              </ContextMenu>
            </div>
            <div
              className={classNames(s.contentWrapper, {
                [s.oddRows]: results.length % 2 !== 0,
              })}
              style={{ width: totalContentWidth }}
            >
              <InfiniteList
                isItemLoaded={isItemLoaded}
                itemCount={totalVirtualItemCount + results.length}
                loadMoreItems={isNextPageLoading ? promiseNoop : loadMore}

                itemSize={itemSize || 110}
                itemData={itemRendererProps}
                itemRenderer={StreamingServiceSearchResultRenderer}
              />
            </div>
          </div>
        </PlaylistControls>
      )}
    </article>
  );
}

export default React.memo(StreamingServiceSearchResults) as typeof StreamingServiceSearchResults;
